import { Transition } from "@headlessui/react";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import invoiceCredit from "../../../assets/images/icon_invoice/InvoiceCredit.svg";
import invoiceNonCredit from "../../../assets/images/icon_invoice/InvoiceNonCredit.svg";
import invoiceCreditPartial from "../../../assets/images/icon_invoice/InvoiceCreditPartial.svg";
import autoDebetPartial from "../../../assets/images/icon_invoice/icon_autodebet/perdetail.svg";
import autoDebetNonCredit from "../../../assets/images/icon_invoice/icon_autodebet/noncredit.svg";
import autoDebetCredit from "../../../assets/images/icon_invoice/icon_autodebet/credit.svg";
import diskon from "../../../assets/images/icon_invoice/discount.svg";
import { isDarkColor } from "../../../config/DarkColorChecker";
import { formatCurrency } from "../../../config/global";
import { setLoading } from "../../../core/feature/config/configSlice";
import { useAppSelector } from "../../../core/feature/hooks";
import CustomApp from "../../../core/models/customApp";
import {
  getAllInvoice,
  getInvoiceTags,
} from "../../../core/service/api_invoice";
import moment from "moment";
import { InvoiceDataItem } from "./InvoiceWebTypes";
import User from "../../../core/models/user";
import Select from "react-select";

type InvoiceStatus = "UNPAID" | "PARTIAL" | "PAID" | (string & {});
type NotificationStatus = "QUEUE" | "NONE" | (string & {});
type MinimumPaymentPosition = "SUMMARY" | "ITEM" | (string & {});
type MinimumPaymentType = "CURRENCY" | (string & {});
type SortField = "invoiceDate" | "dueDate" | "amount";
type SortDirection = "Asc" | "Desc";
type SortOptionValue = `${SortField}_${SortDirection}`;

type SortMetadata = {
  sorted: boolean;
  unsorted: boolean;
  empty: boolean;
};

type PageableMetadata = {
  sort: SortMetadata;
  pageNumber: number;
  pageSize: number;
  offset: number;
  paged: boolean;
  unpaged: boolean;
};

type PaginatedResponse<T> = {
  content: T[];
  pageable?: PageableMetadata;
  last?: boolean;
  totalPages?: number;
  totalElements?: number;
  sort?: SortMetadata;
  first?: boolean;
  size?: number;
  number?: number;
  numberOfElements?: number;
  empty?: boolean;
};

type InvoiceListApiResponse = {
  data?: PaginatedResponse<Invoice>;
};

type SortOption = {
  label: string;
  value: SortOptionValue;
};

const EMPTY_INVOICE_RESPONSE: PaginatedResponse<Invoice> = {
  content: [],
  totalElements: 0,
};

export interface Invoice {
  id: string;
  title: string;
  automaticPayment: string;
  companyId: string;
  invoiceDate: string;
  createDate: string;
  dueDate: string;
  description: string;
  repeat: boolean;
  showDetail: boolean;
  partialMethod: boolean;
  invoiceId: string;
  invoiceMasterId: string;
  callerId: string;
  callerName: string;
  amount: number;
  paidAmount: number;
  detail: InvoiceDetail[];
  status: InvoiceStatus;
  history: InvoiceHistory[];
  notificationStatusCreate: NotificationStatus;
  notificationStatusDueDate: NotificationStatus;
  minimumPayment: number;
  minimumPaymentPosition: MinimumPaymentPosition;
  minimumPaymentType: MinimumPaymentType;
  tags: string[];
  creditDetailPayment: boolean | null;
  clientInvoiceId: string | null;
  isPublished: boolean;
  publishedAt: string | null;
  masterTags: string[];
  paymentAll: boolean;
  addByTag: boolean;
}

export interface InvoiceDetail {
  id: string;
  title: string;
  count: number;
  price: number;
  amount: number;
  discount: number;
  coaCode: string;
  paidAmount: number;
}

export interface InvoiceHistory {
  dateTime: string;
  amount: number;
  transactionId: string;
  channel: string;
  reference: InvoiceReference[];
}

export interface InvoiceReference {
  title: string;
  detailId: string;
  amount: number;
}

interface InvoiceTabComponentProps {
  onSelectItem: (item: InvoiceDataItem) => void;
}

export const InvoiceTabComponent = ({
  onSelectItem,
}: InvoiceTabComponentProps) => {
  const { t } = useTranslation();
  const invoiceTitle = t("INVOICE.title");
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [data, setData] = useState<Invoice[]>([]);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(10);
  const [page, setPage] = useState(0);
  const [showSort, setShowSort] = useState(false);
  const [showFilter, setShowFilter] = useState(false);

  const [sortBy, setSortBy] = useState<SortOptionValue>("invoiceDate_Desc");
  const user: User = useAppSelector((state) => state.user.user);
  const customApp: CustomApp = useAppSelector(
    (state) => state.customApp.customApp,
  );
  const callerIdentities = user?.accounts?.[0]?.callerIdentities ?? [];
  const singleCallerIdentityId =
    callerIdentities.length === 1
      ? String(callerIdentities[0]?.id ?? callerIdentities[0]?.callerId ?? "")
      : "";
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState(
    singleCallerIdentityId,
  );
  const [draftTags, setDraftTags] = useState<string[]>([]);
  const [draftStudentId, setDraftStudentId] = useState(singleCallerIdentityId);
  const [filterTagOptions, setFilterTagOptions] = useState<string[]>([]);
  const tagSelectOptions = filterTagOptions.map((tag) => ({
    label: tag,
    value: tag,
  }));
  const [fitlerInvoiceName, setFilterInvoiceName] = useState<string>("");

  const selectMenuPortalTarget =
    typeof window !== "undefined" ? document.body : undefined;

  const tagSelectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: 48,
      borderRadius: 12,
      borderColor: "#cbd5e1",
      boxShadow: "none",
    }),
    menuPortal: (base: any) => ({
      ...base,
      zIndex: 70,
    }),
    menu: (base: any) => ({
      ...base,
      zIndex: 70,
    }),
    menuList: (base: any) => ({
      ...base,
      maxHeight: 240,
      overflowY: "auto",
    }),
    multiValue: (base: any) => ({
      ...base,
      backgroundColor: customApp?.app_accent_color,
      color: isDarkColor(customApp?.app_accent_color) ? "#ffffff" : "#000000",
    }),
    multiValueLabel: (base: any) => ({
      ...base,
      color: isDarkColor(customApp?.app_accent_color) ? "#ffffff" : "#000000",
      textTransform: "capitalize",
    }),
    multiValueRemove: (base: any) => ({
      ...base,
      color: isDarkColor(customApp?.app_accent_color) ? "#ffffff" : "#000000",
    }),
  };

  useEffect(() => {
    getInvoiceTags()
      .then((res: any) => {
        setFilterTagOptions(res.data ?? []);
      })
      .catch(() => {
        setFilterTagOptions([]);
      });
  }, []);

  const sortingOptions = useMemo<SortOption[]>(
    () => [
      { label: t("SORTING.invoiceDateDesc"), value: "invoiceDate_Desc" },
      { label: t("SORTING.invoiceDateAsc"), value: "invoiceDate_Asc" },
      { label: t("SORTING.dueDateAsc"), value: "dueDate_Asc" },
      { label: t("SORTING.dueDateDesc"), value: "dueDate_Desc" },
      { label: t("SORTING.amountAsc"), value: "amount_Asc" },
      { label: t("SORTING.amountDesc"), value: "amount_Desc" },
    ],
    [t],
  );

  const openSortDialog = () => {
    setShowSort(true);
  };

  const dismissSortDialog = () => {
    setShowSort(false);
  };

  const handlePageSizeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    setSize(Number(event.target.value));
    setPage(0);
  };

  const handleSortChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSortBy(event.target.value as SortOptionValue);
  };

  const resolveStudentId = (identityId = selectedStudentId) =>
    identityId || singleCallerIdentityId;

  const getSelectedCallerIds = (identityId = selectedStudentId) => {
    const resolvedIdentityId = resolveStudentId(identityId);
    const selectedIdentity = callerIdentities.find((identity: any) => {
      const identityValue = identity?.id ?? identity?.callerId ?? "";
      return String(identityValue) === String(resolvedIdentityId);
    });

    return selectedIdentity?.callerId ? [selectedIdentity.callerId] : [];
  };

  const hasDiscount = (invoice: Invoice) =>
    invoice.detail.some((detailItem) => detailItem.discount !== 0);

  const getIconInvoice = (
    item: Pick<
      Invoice,
      "partialMethod" | "creditDetailPayment" | "automaticPayment"
    >,
  ) => {
    const { automaticPayment, partialMethod, creditDetailPayment } = item;

    // autodebet
    if (automaticPayment) {
      if (!partialMethod) return autoDebetNonCredit;
      return !creditDetailPayment ? autoDebetCredit : autoDebetPartial;
    }

    // maunal payment
    if (!partialMethod) return invoiceNonCredit;
    return !creditDetailPayment ? invoiceCredit : invoiceCreditPartial;
  };

  const openFilterDialog = () => {
    setDraftTags(selectedTags);
    setDraftStudentId(resolveStudentId());
    setShowFilter(true);
  };

  const dismissFilterDialog = () => {
    setShowFilter(false);
  };

  const applyFilters = () => {
    const nextStudentId = resolveStudentId(draftStudentId);

    setSelectedTags(draftTags);
    setSelectedStudentId(nextStudentId);
    setPage(0);
    getData(draftTags, getSelectedCallerIds(nextStudentId), fitlerInvoiceName);
    dismissFilterDialog();
  };

  const resetFilters = () => {
    const nextStudentId = singleCallerIdentityId;

    setDraftTags([]);
    setDraftStudentId(nextStudentId);
    setSelectedTags([]);
    setSelectedStudentId(nextStudentId);
    setFilterInvoiceName("");
    setPage(0);
    getData();
    dismissFilterDialog();
  };

  const getData = async (
    tags: string[] = [],
    studentId: string[] = [],
    invoiceName: string = "",
  ) => {
    dispatch(setLoading(true));
    const [sortField, sortDirection] = sortBy.split("_") as [
      SortField,
      SortDirection,
    ];

    try {
      const response = (await getAllInvoice(
        size,
        page,
        sortField,
        sortDirection,
        "",
        "",
        tags,
        studentId,
        invoiceName,
      )) as InvoiceListApiResponse;
      const invoiceResponse = response.data ?? EMPTY_INVOICE_RESPONSE;

      setData(invoiceResponse.content ?? []);
      setTotal(invoiceResponse.totalElements ?? 0);
    } catch {
      navigate("/login", { replace: true });
    } finally {
      dispatch(setLoading(false));
    }
  };

  useEffect(() => {
    const fetchInvoices = async () => {
      dispatch(setLoading(true));
      const [sortField, sortDirection] = sortBy.split("_") as [
        SortField,
        SortDirection,
      ];

      try {
        const response = (await getAllInvoice(
          size,
          page,
          sortField,
          sortDirection,
          "",
          "",
        )) as InvoiceListApiResponse;
        const invoiceResponse = response.data ?? EMPTY_INVOICE_RESPONSE;

        setData(invoiceResponse.content ?? []);
        setTotal(invoiceResponse.totalElements ?? 0);
      } catch {
        navigate("/login", { replace: true });
      } finally {
        dispatch(setLoading(false));
      }
    };

    fetchInvoices();
  }, [dispatch, navigate, page, size, sortBy]);

  return (
    <>
      <section>
        <div className="container">
          <div className="container rounded-xl px-3 ">
            <div className="mb-4 flex flex-wrap gap-3">
              <button
                className="px-3 py-2.5 bg-white border-1 rounded-full text-dark flex flex-row gap-3"
                onClick={() => openSortDialog()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-6"
                >
                  <path
                    fillRule="evenodd"
                    d="M6.97 2.47a.75.75 0 0 1 1.06 0l4.5 4.5a.75.75 0 0 1-1.06 1.06L8.25 4.81V16.5a.75.75 0 0 1-1.5 0V4.81L3.53 8.03a.75.75 0 0 1-1.06-1.06l4.5-4.5Zm9.53 4.28a.75.75 0 0 1 .75.75v11.69l3.22-3.22a.75.75 0 1 1 1.06 1.06l-4.5 4.5a.75.75 0 0 1-1.06 0l-4.5-4.5a.75.75 0 1 1 1.06-1.06l3.22 3.22V7.5a.75.75 0 0 1 .75-.75Z"
                    clipRule="evenodd"
                  />
                </svg>
                {t("INVOICE.sort")}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-6"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                  />
                </svg>
              </button>

              <button
                className="px-3 py-2.5 bg-white border-1 rounded-full text-dark flex flex-row gap-3"
                onClick={() => openFilterDialog()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="size-5"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25v.44a2.25 2.25 0 0 1-.659 1.591l-4.922 4.922a2.25 2.25 0 0 0-.659 1.591v3.152a2.25 2.25 0 0 1-1.006 1.874l-2.25 1.5A2.25 2.25 0 0 1 8 18.45v-4.652a2.25 2.25 0 0 0-.659-1.591L2.66 7.28A2.25 2.25 0 0 1 2 5.69v-.44Z"
                    clipRule="evenodd"
                  />
                </svg>
                Filter
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="m19.5 8.25-7.5 7.5-7.5-7.5"
                  />
                </svg>
              </button>
            </div>
            <div>
              {data.map((item) => {
                const diskonStatus = hasDiscount(item);

                return (
                  <div
                    key={item.id}
                    className="text-dark text-lg mt-2 mb-4 container relative border rounded-xl"
                  >
                    <div className="border-b-2 border-b-slate-700">
                      <div className="inline-flex">
                        <img
                          className="mr-4 mt-3 ml-2"
                          alt=""
                          src={getIconInvoice(item)}
                        />
                        <div className="mt-3 font-semibold text-[1.3vw]">
                          {item.title}
                        </div>
                      </div>
                      {diskonStatus ? (
                        <span className="mt-4 absolute right-0">
                          <img src={diskon} alt="diskon" />
                        </span>
                      ) : null}
                    </div>

                    <div className="flex flex-row relative justify-between">
                      <div className="flex flex-col relative w-4/12 mt-4 pb-4 text-slate-500">
                        <div className="flex flex-row relative">
                          <p className="text-[1vw]">{t("INVOICE.name")}</p>
                        </div>
                        <div className="flex flex-row relative">
                          <p className="text-[1vw]">{t("INVOICE.nis")}</p>
                        </div>

                        <div className="flex flex-row relative">
                          <p className="text-[1vw]">{t("INVOICE.date")}</p>
                        </div>
                      </div>
                      <div className="flex flex-col relative w-5/12 mt-4 pb-4 font-semibold">
                        <div className="flex flex-row relative">
                          <p className="text-[1vw]">
                            {" : "} {item.callerName}
                          </p>
                        </div>
                        <div className="flex flex-row relative">
                          <p className="text-[1vw]">
                            {" : "} {item.callerId}
                          </p>
                        </div>

                        <div className="flex flex-row relative">
                          <p className="text-[1vw]">
                            {" : "}
                            {moment(item.invoiceDate).format("DD MMMM YYYY")}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col relative w-5/12 mt-4 pb-4 text-slate-500">
                        <div className="contents">
                          <div className="flex flex-row relative">
                            <p className="text-[1vw]">{t("INVOICE.credit")}</p>
                          </div>
                          <div className="flex flex-row relative">
                            <p className="text-[1vw]">{t("INVOICE.paid")}</p>
                          </div>
                          <div className="flex flex-row relative">
                            <p className="text-[1vw]">{t("INVOICE.limit")}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col relative w-5/12 mt-4 pb-4 font-semibold">
                        <div className="contents">
                          <div className="flex flex-row relative">
                            <p className="text-[1vw]">
                              {" : "}
                              {item.partialMethod === false
                                ? t("OTHER.no")
                                : t("OTHER.yes")}
                            </p>
                          </div>
                          <div className="flex flex-row relative">
                            <p className="text-[1vw]">
                              {" : "} {formatCurrency(item.paidAmount)}
                            </p>
                          </div>
                          <div className="flex flex-row relative">
                            <p className="text-[1vw]">
                              {" : "}
                              {moment(item.dueDate).format("DD MMMM YYYY")}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-col relative w-2/12 mt-4 pb-4 items-end">
                        <h4 className="text-slate-400 text-[1vw]">
                          {t("INVOICE.sisa")}
                        </h4>
                        <p className="font-bold text-[1.5vw]">
                          {formatCurrency(item.amount - item.paidAmount)}
                        </p>
                        <button
                          onClick={() => onSelectItem(item)}
                          style={{
                            backgroundColor: customApp?.app_accent_color,
                          }}
                          className={`h-[5vh] w-[10vw] rounded-xl hover:opacity-80 ${
                            isDarkColor(customApp?.app_accent_color)
                              ? "text-white"
                              : "text-black"
                          }`}
                        >
                          {t("INVOICE.pay")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex relative mt-10 justify-between">
                <div className="pt-2 w-1/4">
                  <p>
                    {t("OTHER.showing")} {page + 1} {t("OTHER.to")} {size}{" "}
                    {t("OTHER.from")} {total} data
                  </p>
                </div>
                <div className="w-auto">
                  <div className="">
                    <select
                      onChange={handlePageSizeChange}
                      value={size}
                      className="py-2 border rounded-lg bg-white"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={1000}>1000</option>
                    </select>
                    <span className="ml-1">{t("OTHER.entry")}</span>
                  </div>
                </div>
                <div className="flex w-1/4">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 0}
                    className="disabled:bg-slate-200 disabled:text-slate-400 border mr-2 bg-blue-300 text-blue-600 rounded-md w-36 hover:text-white hover:bg-psp-hover"
                  >
                    {t("OTHER.previous")}
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={(page + 1) * size > total}
                    className="disabled:bg-slate-200 disabled:text-slate-400 border mr-2 bg-blue-300 text-blue-600 rounded-md w-36 hover:text-white hover:bg-psp-hover"
                  >
                    {t("OTHER.next")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Transition
          show={showSort}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <>
            <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
              <div className="relative w-full my-6 mx-auto max-w-3xl">
                <div className="border-0 rounded-2xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                  <div className="relative p-6 flex-auto">
                    <div>
                      <div className="flex pt-2 mb-3">
                        <div className="w-full">
                          <p className="font-bold text-xl">
                            {t("INVOICE.sort")}
                          </p>
                          <div className="">
                            <div className="flex flex-column gap-2">
                              {sortingOptions.map((sort) => (
                                <label
                                  key={sort.value}
                                  htmlFor={sort.value}
                                  className="p-4 border-b border-b-silver flex justify-between hover:bg-psp-hover hover:rounded-lg hover:text-white"
                                >
                                  {sort.label}
                                  <input
                                    onChange={handleSortChange}
                                    id={sort.value}
                                    name="selectSortBy"
                                    checked={sortBy === sort.value}
                                    className="w-5 h-5 border-2 border-psp-primary rounded-full"
                                    style={{
                                      accentColor: customApp?.app_accent_color,
                                    }}
                                    type="radio"
                                    value={sort.value}
                                  />
                                </label>
                              ))}
                            </div>
                            <button
                              className="bg-danger w-full p-2 text-white rounded-lg"
                              onClick={dismissSortDialog}
                            >
                              Close
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
          </>
        </Transition>

        {/* Filter */}
        <Transition
          show={showFilter}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <>
            <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
              <div className="relative w-full my-6 mx-auto max-w-3xl">
                <div className="border-0 rounded-2xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
                  <div className="relative p-6 flex-auto">
                    <div className="flex items-start justify-between gap-6">
                      <div className="w-full">
                        <p className="font-bold text-xl mb-4">
                          {t("INVOICE.filter")}
                        </p>
                        <div className="mb-6">
                          <p className="font-semibold text-base mb-3">
                            {t("INVOICE.filterTags")}
                          </p>
                          <Select
                            options={tagSelectOptions}
                            isClearable
                            menuPortalTarget={selectMenuPortalTarget}
                            menuPosition="fixed"
                            menuPlacement="auto"
                            maxMenuHeight={240}
                            value={
                              tagSelectOptions.find(
                                (option) => option.value === draftTags[0],
                              ) ?? null
                            }
                            onChange={(selectedOption: any) =>
                              setDraftTags(
                                selectedOption?.value
                                  ? [selectedOption.value]
                                  : [],
                              )
                            }
                            placeholder={t("INVOICE.selectTag")}
                            className="text-sm"
                            styles={tagSelectStyles}
                          />
                        </div>
                        {/* <div className="mb-6">
                          <p className="font-semibold text-base mb-3">
                            {t("INVOICE.invoiceName")}
                          </p>
                          <input
                            type="text"
                            value={fitlerInvoiceName}
                            onChange={(e) =>
                              setFilterInvoiceName(e.target.value)
                            }
                            className="px-3 grow rounded-lg w-full h-10 border-1 border-psp-gray border-solid disabled:text-psp-nav active:border-psp-gray focus:border-psp-gray active:ring-0 focus:ring-0"
                          />
                        </div> */}
                        {callerIdentities.length !== 1 && (
                          <div className="mb-6">
                            <label
                              htmlFor="invoice-student-filter"
                              className="font-semibold text-base mb-3 block"
                            >
                              {t("INVOICE.studentName")}
                            </label>
                            <select
                              id="invoice-student-filter"
                              value={draftStudentId}
                              onChange={(event) =>
                                setDraftStudentId(event.target.value)
                              }
                              className="w-full rounded-xl border bg-white border-slate-300 px-4 py-3 text-sm"
                            >
                              <option value="">
                                {t("INVOICE.allStudents")}
                              </option>
                              {callerIdentities.map((identity: any) => {
                                const identityValue =
                                  identity?.id ?? identity?.callerId ?? "";

                                return (
                                  <option
                                    key={identityValue}
                                    value={String(identityValue)}
                                  >
                                    {identity?.name ?? identity?.callerId}
                                  </option>
                                );
                              })}
                            </select>
                          </div>
                        )}
                        <div className="flex justify-end gap-3">
                          <button
                            type="button"
                            onClick={() => resetFilters()}
                            className="rounded-xl border border-slate-300 px-5 py-2.5 text-slate-600"
                          >
                            {t("OTHER.reset")}
                          </button>
                          <button
                            type="button"
                            onClick={() => applyFilters()}
                            style={{
                              backgroundColor: customApp?.app_accent_color,
                            }}
                            className={`rounded-xl px-5 py-2.5 ${
                              isDarkColor(customApp?.app_accent_color)
                                ? "text-white"
                                : "text-black"
                            }`}
                          >
                            {t("OTHER.apply")}
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => dismissFilterDialog()}
                        className="text-2xl font-bold text-gray-400 hover:text-gray-600"
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              className="opacity-25 fixed inset-0 z-40 bg-black"
              onClick={() => dismissFilterDialog()}
            ></div>
          </>
        </Transition>
      </section>
    </>
  );
};
